
import { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../hooks/useToast';

export default function Login() {
  const { t } = useTranslation('auth');
  const { showToast, ToastContainer } = useToast();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isSignUp) {
        const { error } = await supabase.auth.signUp({
          email,
          password,
        });
        if (error) throw error;
        showToast(t('signupSuccess'), 'success');
      } else {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (error) throw error;
        showToast(t('loginSuccess'), 'success');
        setTimeout(() => {
          window.REACT_APP_NAVIGATE('/dashboard');
        }, 500);
      }
    } catch (err: unknown) {
      if (err instanceof Error) {
        const errorMessages: { [key: string]: string } = {
          'Invalid login credentials': t('errors.invalidCredentials'),
          'Email not confirmed': t('errors.emailNotConfirmed'),
          'User already registered': t('errors.userExists'),
        };
        showToast(errorMessages[err.message] || err.message, 'error');
      } else {
        showToast(t('errors.general'), 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <ToastContainer />
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-teal-50/30 to-gray-50 flex items-center justify-center px-6 py-12">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <div className="inline-flex items-center space-x-2 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center">
                <i className="ri-qr-code-line text-white text-2xl"></i>
              </div>
              <span className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">LinkQR</span>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {isSignUp ? t('signupTitle') : t('loginTitle')}
            </h1>
            <p className="text-gray-600">
              {isSignUp ? t('signupSubtitle') : t('loginSubtitle')}
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8">
            <form onSubmit={handleAuth} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('email')}
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
                  placeholder="your@email.com"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('password')}
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
                  placeholder="••••••••"
                  required
                  minLength={6}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full px-6 py-3 bg-gradient-to-r from-teal-500 to-teal-600 text-white font-semibold rounded-lg hover:shadow-lg hover:shadow-teal-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
              >
                {loading && <i className="ri-loader-4-line animate-spin"></i>}
                {loading ? t('processing') : isSignUp ? t('signupButton') : t('loginButton')}
              </button>
            </form>

            <div className="mt-6 text-center">
              <button
                onClick={() => setIsSignUp(!isSignUp)}
                className="text-sm text-teal-600 hover:text-teal-700 font-medium cursor-pointer"
              >
                {isSignUp ? t('switchToLogin') : t('switchToSignup')}
              </button>
            </div>
          </div>

          <div className="mt-6 text-center">
            <a href="/" className="text-sm text-gray-600 hover:text-teal-600 cursor-pointer">
              ← {t('backToHome')}
            </a>
          </div>
        </div>
      </div>
    </>
  );
}
